import { setCookie, parseCookies, destroyCookie } from 'nookies';

const STORAGE_KEY = 'registration_form_data';
const COOKIE_OPTIONS = {
  maxAge: 7 * 24 * 60 * 60, // 7 days
  path: '/',
  sameSite: 'lax' as const,
};

export interface RegistrationData {
  // Step 0 - Basic Info
  email?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  termOfUse?: boolean;
  
  // Step 1 - Company Details
  companyName?: string;
  industry?: string;
  website?: string;
  
  // Step 2 - Contact & Location
  phone?: string;
  phone_verification?: string;
  location?: any;
  timezone?: string;
  category?: string;
  
  // Meta information
  activeStep?: number;
  currentField?: number;
  isQuickStart?: boolean;
  isPhoneVerified?: boolean;
}

/**
 * Save registration data to both cookies and localStorage
 */
export const saveRegistrationData = (data: RegistrationData): void => {
  try {
    // Save to localStorage
    const existingData = loadRegistrationData();
    const mergedData = { ...existingData, ...data };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedData));
    
    // Save individual fields to cookies for better reliability
    Object.entries(mergedData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Don't save password in cookies for security
        if (key !== 'password') {
          setCookie(null, `reg_${key}`, JSON.stringify(value), COOKIE_OPTIONS);
        }
      }
    });
    
    // Save password separately in localStorage only (more secure)
    if (data.password !== undefined) {
      localStorage.setItem(`${STORAGE_KEY}_pwd`, data.password);
    }
  } catch (error) {
    console.error('Error saving registration data:', error);
  }
};

/**
 * Load registration data from cookies and localStorage
 */
export const loadRegistrationData = (): RegistrationData => {
  try {
    let data: RegistrationData = {};
    
    // Try to load from localStorage first
    const localStorageData = localStorage.getItem(STORAGE_KEY);
    if (localStorageData) {
      try {
        data = JSON.parse(localStorageData);
      } catch (e) {
        console.error('Error parsing localStorage data:', e);
      }
    }
    
    // Load password from separate localStorage key
    const password = localStorage.getItem(`${STORAGE_KEY}_pwd`);
    if (password) {
      data.password = password;
    }
    
    // Also check cookies as fallback and merge
    const cookies = parseCookies();
    Object.keys(cookies).forEach(key => {
      if (key.startsWith('reg_')) {
        const fieldName = key.replace('reg_', '') as keyof RegistrationData;
        try {
          const value = JSON.parse(cookies[key]);
          // Only use cookie value if localStorage doesn't have it
          if (data[fieldName] === undefined) {
            (data as any)[fieldName] = value;
          }
        } catch (e) {
          // If JSON parse fails, use as string
          if (data[fieldName] === undefined) {
            (data as any)[fieldName] = cookies[key];
          }
        }
      }
    });
    
    return data;
  } catch (error) {
    console.error('Error loading registration data:', error);
    return {};
  }
};

/**
 * Clear all registration data from cookies and localStorage
 */
export const clearRegistrationData = (): void => {
  try {
    // Clear localStorage
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(`${STORAGE_KEY}_pwd`);
    
    // Clear cookies
    const cookies = parseCookies();
    Object.keys(cookies).forEach(key => {
      if (key.startsWith('reg_')) {
        destroyCookie(null, key, { path: '/' });
      }
    });
  } catch (error) {
    console.error('Error clearing registration data:', error);
  }
};

/**
 * Determine the appropriate step based on available data
 */
export const determineCurrentStep = (data: RegistrationData): {
  activeStep: number;
  currentField: number;
  isQuickStart: boolean;
} => {
  // If we have explicit step information, use it
  if (data.activeStep !== undefined) {
    return {
      activeStep: data.activeStep,
      currentField: data.currentField || 0,
      isQuickStart: data.isQuickStart ?? true,
    };
  }
  
  // Otherwise, determine based on what data is available
  // Check Step 2 fields
  if (data.phone || data.location) {
    return { activeStep: 2, currentField: 0, isQuickStart: false };
  }
  
  // Check Step 1 fields  
  if (data.companyName || data.industry || (data.website && !data.isQuickStart)) {
    return { activeStep: 1, currentField: 0, isQuickStart: false };
  }
  
  // Check Step 0 fields
  if (data.termOfUse) {
    return { activeStep: 0, currentField: 4, isQuickStart: false };
  }
  if (data.password) {
    return { activeStep: 0, currentField: 3, isQuickStart: false };
  }
  if (data.lastName) {
    return { activeStep: 0, currentField: 2, isQuickStart: false };
  }
  if (data.firstName) {
    return { activeStep: 0, currentField: 1, isQuickStart: false };
  }
  if (data.email) {
    return { activeStep: 0, currentField: 0, isQuickStart: false };
  }
  
  // If only website is provided (quick start scenario)
  if (data.website) {
    return { activeStep: 0, currentField: 0, isQuickStart: true };
  }
  
  // Default to beginning
  return { activeStep: 0, currentField: 0, isQuickStart: true };
};

/**
 * Check if user has completed initial registration (has access token)
 */
export const hasCompletedInitialRegistration = (): boolean => {
  try {
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      return !!user.access_token;
    }
  } catch (error) {
    console.error('Error checking registration status:', error);
  }
  return false;
};